import { parseNumber } from '@progress/kendo-intl';
import { PDFViewerHandle, scrollToPage } from '@progress/kendo-react-pdf-viewer';

import axios from 'axios';
import { isEmpty, round } from 'lodash';
import { PDFDocument, PDFPage, rgb } from 'pdf-lib';
import { createRef, FC, useCallback, useEffect, useState } from 'react';
import ImagePreview from 'src/components/Image';
import PDFViewer from 'src/components/PDFViewer';
import useWindowSize from 'src/hooks/useWindowSize';
import { OcrInvoiceResponse } from 'src/services/useGetOcrResponseApi';
import { uint8ArrayToBase64 } from 'src/utils/fileTransformation';
import { formatOcrData } from 'src/utils/transformation';

export interface IOcrData {
  field: string;
  value: string | null;
  pageNumber: string | null;
  boundingBox: number[] | null;
  confidence: string | null;
  isHighlight?: boolean;
}
interface OcrInvoiceProps {
  src: string;
  data: OcrInvoiceResponse;
  isPDF: boolean;
  fileName: string;
  invoiceOcrConfidence: string | null;
}

const pdfWidth = 595; // this width OCR service using for convert pdf
const pdfHeight = 841; // this height OCR service using for convert pdf

const getPdfWidthAndHeightBasedOnRotation = (page: PDFPage) => {
  const { width, height } = page.getSize();
  const rotation = page.getRotation().angle;
  if ((rotation / 90) % 2 === 1) {
    return {
      width: height,
      height: width,
    };
  }
  return {
    width,
    height,
  };
};

export const ocrInvoiceFields = [
  'invoiceNumber',
  'purchaseOrder',
  'invoiceDate',
  'amountDue',
  'totalTax',
  'totalDiscount',
  'totalShipping',
];

const OcrInvoice: FC<OcrInvoiceProps> = ({ src, data, isPDF, fileName, invoiceOcrConfidence }) => {
  const { height, width } = useWindowSize();
  const [pdfData, setPdfData] = useState(''); // base64 bbox data
  const [pdfUrl, setPdfUrl] = useState(''); // original pdf url
  const [imageUrl, setImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [highlightPage, setHighlightPage] = useState(1);
  const pdfRef = createRef<PDFViewerHandle>();

  const getUrl = useCallback(async (src: string) => {
    const response = await axios.get(src, { responseType: 'blob' });
    const blob: Blob = response.data;
    return window.URL.createObjectURL(blob);
  }, []);

  const generateImageWithBoundingBox = useCallback(
    async (src: string, data: OcrInvoiceResponse) => {
      const url = await getUrl(src);
      const canvas = document.createElement('canvas');
      canvas.setAttribute('class', 'canv');
      const ctx = canvas?.getContext('2d') as CanvasRenderingContext2D;

      const image = new Image();
      image.src = url;
      image.onload = () => {
        // Set canvas size to the image size
        canvas.width = image.width;
        canvas.height = image.height;

        // Draw the image on the canvas
        ctx.drawImage(image, 0, 0);

        // Draw the bounding boxes
        const formattedOcrData = formatOcrData(data);
        formattedOcrData.forEach((item) => {
          if (item.boundingBox) {
            const isMatchConfidence =
              parseNumber(item.confidence || '0%') >= parseNumber(invoiceOcrConfidence || '0%');

            // converting the coordinate into px base on the OCR service pdf Width and Height
            const mappedBox = item.boundingBox.map((value, index) => {
              if (index % 2 === 0) {
                return (value = ((value * 72) / pdfWidth) * canvas.width);
              } else {
                return (value = ((value * 72) / pdfHeight) * canvas.height);
              }
            });

            const [x1, y1, x2, y2, x3, y3, x4, y4] = mappedBox;

            // Calculate bounding rectangle
            const minX = Math.min(x1, x2, x3, x4);
            const minY = Math.min(y1, y2, y3, y4);
            const maxX = Math.max(x1, x2, x3, x4);
            const maxY = Math.max(y1, y2, y3, y4);
            let width = maxX - minX;
            let height = maxY - minY;
            let x = minX;
            let y = minY;
            if (item.isHighlight) {
              const increasedWidth = width * 1.5 - width;
              const increasedHeight = height * 1.5 - height;
              width = width + increasedWidth;
              height = height + increasedHeight;
              x = x - increasedWidth / 2;
              y = y - increasedHeight / 2;
            }
            ctx.strokeStyle = item.isHighlight
              ? '#FF00E6'
              : isMatchConfidence
              ? '#94C947'
              : '#D9AA4F';
            ctx.lineWidth = item.isHighlight ? 2 : 1; // Set border thickness
            ctx.strokeRect(x, y, width, height);
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
          }
        });
        const img = canvas.toDataURL('image/png', 1);
        setImageUrl(img);
      };
      setIsLoading(false);
    },
    [getUrl, invoiceOcrConfidence]
  );

  const generatePDFWithBoundingBox = useCallback(
    async (src: string, data: OcrInvoiceResponse) => {
      const PdfArrayBufferBytes = await fetch(src).then((res) => res.arrayBuffer());
      const blob: Blob = new Blob([PdfArrayBufferBytes], { type: 'application/pdf' });
      setPdfUrl(window.URL.createObjectURL(blob));
      const pdf = await PDFDocument.load(PdfArrayBufferBytes);
      const pages = pdf.getPages();
      const formattedOcrData = formatOcrData(data);

      for (let i = 1; i <= pages.length; i++) {
        const page = pages[i - 1];
        const { width: pageWidth, height: pageHeight } = getPdfWidthAndHeightBasedOnRotation(page);
        const rotation = page.getRotation().angle;

        formattedOcrData.forEach((item) => {
          if (Number(item.pageNumber) === i && item.boundingBox) {
            const mappedBox = item.boundingBox.map((value) => value * 72); // convert inch to pt
            const [x1, y1, x2, y2, x3, y3, x4, y4] = mappedBox;
            const isMatchConfidence =
              parseNumber(item.confidence || '0%') >= parseNumber(invoiceOcrConfidence || '0%');

            // Calculate rectangle's top-left corner, width, and height
            const minX = Math.min(x1, x2, x3, x4);
            const maxX = Math.max(x1, x2, x3, x4);
            const minY = Math.min(y1, y2, y3, y4);
            const maxY = Math.max(y1, y2, y3, y4);
            let width = Math.abs(maxX - minX);
            let height = Math.abs(maxY - minY);

            let x, y;
            // convert the x,y,width and height coordinate based on pdf page rotation
            switch (rotation) {
              case 0:
                x = minX;
                y = pageHeight - minY - height;
                break;
              case 90:
                x = minY;
                y = minX;
                [width, height] = [height, width];
                break;
              case 180:
                x = pageWidth - minX - width;
                y = minY;
                break;
              case 270:
                x = pageHeight - minY - height;
                y = pageWidth - minX - width;
                [width, height] = [height, width];
                break;
              default:
                x = minX;
                y = pageHeight - minY - height;
            }

            if (item.isHighlight) {
              const increasedWidth = width * 1.5 - width;
              const increasedHeight = height * 1.5 - height;
              width = width + increasedWidth;
              height = height + increasedHeight;
              x = x - increasedWidth / 2;
              y = y - increasedHeight / 2;
              setHighlightPage(Number(item.pageNumber));
            }
            page.drawRectangle({
              x: round(x, 2),
              y: round(y, 2),
              width: round(width, 2),
              height: round(height, 2),
              borderColor: item.isHighlight
                ? rgb(1, 0, 0.9)
                : isMatchConfidence
                ? rgb(0.58, 0.78, 0.27)
                : rgb(0.85, 0.66, 0.3),
              borderWidth: item.isHighlight ? 2 : 1,
            });
          }
        });
      }

      const pdfBytesWithBoundingBoxes = await pdf.save();
      const dataUrl = uint8ArrayToBase64(pdfBytesWithBoundingBoxes);
      setPdfData(dataUrl);
      setIsLoading(false);
    },
    [invoiceOcrConfidence]
  );

  useEffect(() => {
    if (src && !isEmpty(data)) {
      if (isPDF) {
        generatePDFWithBoundingBox(src, data);
      } else {
        generateImageWithBoundingBox(src, data);
      }
    }
  }, [isPDF, generateImageWithBoundingBox, generatePDFWithBoundingBox, src, data]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!isLoading) {
      timer = setTimeout(() => {
        if (pdfRef.current) {
          scrollToPage(pdfRef?.current?.element as HTMLElement, highlightPage - 1);
        }
      }, 300);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [highlightPage, isLoading, pdfRef]);

  return (
    <>
      {isLoading ? (
        <div className="w-full h-24 flex items-center justify-center">
          <span className="k-icon k-i-loading text-xl" />
        </div>
      ) : isPDF ? (
        <PDFViewer
          data={pdfData}
          url={pdfUrl}
          height={`${height - 125}px`}
          fileName={fileName}
          ref={pdfRef}
        />
      ) : (
        <ImagePreview
          imageUrl={imageUrl}
          controls
          filename={fileName}
          styles={{
            width: `${width}px`,
            maxHeight: `${height - 210}px`,
          }}
        />
      )}
    </>
  );
};

export default OcrInvoice;
